import { useContactsLight } from "../../Contacts/hooks/useContacts"
import React, { useState, useEffect } from "react"
import i18n from "i18n"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
  Divider,
  CircularProgress,
} from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import CloseIcon from "@mui/icons-material/Close"
import DeleteIcon from "@mui/icons-material/Delete"
import PropTypes from "prop-types"
import CostItem from "./CostItem"
import { v4 as uuidv4 } from "uuid"
import {
  toCents,
  fromCents,
  strictRound2,
  isNearZero,
} from "./utils/costsUtils"
import { useSaveExpenses } from "../hooks/useOrganizationExpenses"
import { useAuthContext } from "context/auth/authContext"

const NewCost = ({ open, onClose, onSave, projectId }) => {
  const [remainingAmounts, setRemainingAmounts] = useState({})

  const { user } = useAuthContext()
  const organizationId = user["custom:organizationId"]

  const getInitialCostData = () => {
    const today = new Date()
    const yyyy = today.getFullYear()
    const mm = String(today.getMonth() + 1).padStart(2, "0")
    const dd = String(today.getDate()).padStart(2, "0")
    const formattedDate = `${yyyy}-${mm}-${dd}`

    return {
      expenseId: uuidv4(),
      organizationId: organizationId,
      id_external: "",
      date: formattedDate,
      title: "",
      projectId: projectId ? [projectId] : [],
      createdByProject: projectId,
      element: "",
      subtotal: 0,
      tax: [],
      retention: [],
      total: 0,
      allocated: 0,
      expense_type: "material",
      is_direct: true,
      providerId: "",
      documents: [],
      notes: "",
    }
  }

  const { data: contactsLight = [] } = useContactsLight(
    organizationId,
    !!organizationId,
  )
  const [costItems, setCostItems] = useState([getInitialCostData()])

  useEffect(() => {
    if (!open) {
      setCostItems([getInitialCostData()])
      setRemainingAmounts({})
    }
  }, [open])

  const handleInputChange = itemIndex => field => event => {
    let value = event.target.value

    if (["subtotal", "total"].includes(field)) {
      value = value === "" ? "" : strictRound2(parseFloat(value) || 0)
    }

    setCostItems(prev =>
      prev.map((item, index) => {
        if (index === itemIndex) {
          const updatedItem = { ...item, [field]: value }

          if (field === "expense_type" && !value) {
            updatedItem.expense_type = "material"
          }
          if (field === "total") {
            const rem = strictRound2(value)
            updatedItem.remainingAmount = isNearZero(rem) ? 0 : rem
            setRemainingAmounts(prev => ({
              ...prev,
              [itemIndex]: isNearZero(rem) ? 0 : rem,
            }))
          }
          return updatedItem
        }
        return item
      }),
    )
  }

  const handleRemainingAmountChange = itemIndex => newRemainingAmount => {
    const norm = isNearZero(newRemainingAmount)
      ? 0
      : strictRound2(newRemainingAmount)
    setRemainingAmounts(prev => ({
      ...prev,
      [itemIndex]: norm,
    }))
  }

  const handleAddCostItem = () => {
    setCostItems(prev => [...prev, getInitialCostData()])
  }

  const handleRemoveCostItem = itemIndex => {
    if (costItems.length > 1) {
      setCostItems(prev => prev.filter((_, index) => index !== itemIndex))
    }
  }

  const createExpenseMutation = useSaveExpenses()

  const handleSave = () => {
    const normalizedItems = costItems.map(item => {
      const total = fromCents(toCents(item.total || 0))
      const subtotal = fromCents(toCents(item.subtotal || 0))
      const cleanTax = Array.isArray(item.tax)
        ? item.tax.map(t => {
            const { active, ...rest } = t || {}
            return rest
          })
        : []
      const cleanRetention = Array.isArray(item.retention)
        ? item.retention.map(r => {
            const { active, ...rest } = r || {}
            return rest
          })
        : []
      let remainingAmount =
        item.remainingAmount !== undefined
          ? fromCents(toCents(item.remainingAmount))
          : total
      if (isNearZero(remainingAmount)) remainingAmount = 0
      return {
        ...item,
        total,
        subtotal,
        tax: cleanTax,
        retention: cleanRetention,
        remainingAmount,
      }
    })
    createExpenseMutation.mutate({ expenses: normalizedItems })
  }

  useEffect(() => {
    if (createExpenseMutation.isSuccess) {
      onClose()
      createExpenseMutation.reset()
    }
  }, [createExpenseMutation.isSuccess, onClose, createExpenseMutation])

  const handleClose = () => {
    onClose()
  }

  const isFormValid = () => {
    const basicValidation = costItems.every(item => {
      return (
        item.date &&
        item.title &&
        item.expense_type &&
        typeof item.is_direct === "boolean" &&
        typeof item.subtotal === "number" &&
        item.subtotal >= 0 &&
        Array.isArray(item.tax) &&
        Array.isArray(item.retention)
      )
    })
    const remainingAmountsValid = Object.values(remainingAmounts).every(
      amount => amount === undefined || isNearZero(amount) || amount >= 0,
    )

    const totalsPositive = costItems.every(
      item => (Number(item.total) || 0) > 0,
    )

    return basicValidation && remainingAmountsValid && totalsPositive
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {i18n.t("costs.modal.newCost")}
        <IconButton
          aria-label={i18n.t("costs.modal.close")}
          onClick={handleClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2, pt: 2 }}>
          {costItems.map((costItem, index) => (
            <Box key={index} sx={{ mb: 3, position: "relative" }}>
              <IconButton
                onClick={() => handleRemoveCostItem(index)}
                // Instead of disabled, use sx to hide the button
                size="small"
                sx={{
                  position: "absolute",
                  top: -10,
                  right: 16,
                  backgroundColor: "background.paper",
                  zIndex: 1,
                  opacity: costItems.length <= 1 ? 0 : 1,
                  pointerEvents: costItems.length <= 1 ? "none" : "auto",
                  "&:hover": {
                    backgroundColor:
                      costItems.length > 1 ? "error.light" : "background.paper",
                    color:
                      costItems.length > 1 ? "error.contrastText" : "disabled",
                  },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
              <CostItem
                formData={costItem}
                handleInputChange={handleInputChange(index)}
                readOnlyAmounts={false}
                contactsLight={contactsLight}
              />
            </Box>
          ))}

          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddCostItem}
            sx={{ mt: 2 }}
          >
            {i18n.t("costs.modal.addCost")}
          </Button>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button variant="outlined" color="primary" onClick={handleClose}>
          {i18n.t("costs.modal.cancel")}
        </Button>
        <Button
          variant="contained"
          color="branded"
          onClick={handleSave}
          sx={{ width: "160px" }}
          disabled={!isFormValid() || createExpenseMutation.isLoading}
        >
          {createExpenseMutation.isLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            i18n.t("costs.modal.generateCost")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

NewCost.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  projectId: PropTypes.string,
}

export default NewCost
